import { createFileRoute, Navigate, Outlet } from "@tanstack/react-router";
import { Button } from "~/components/ui/button";
import { Spinner } from "~/components/ui/spinner";
import { useAuth0Login } from "~/hooks/useAuth0Login";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { useTenant } from "~/hooks/useTenant";

export const Route = createFileRoute("/$tenantSlug")({
  component: TenantPortalRoute,
});

function TenantPortalRoute() {
  const tenant = useTenant();
  const { isAuthenticated, logout } = useAuth0Login();
  const {
    user,
    routing,
    isLoading: isUserLoading,
    isUniversityAccount,
    organizationSlug,
  } = useAuthenticatedUser();

  const hasTenantMismatch =
    tenant.isTenantPortal &&
    !tenant.isLoading &&
    !tenant.error &&
    isAuthenticated &&
    !!user &&
    (!isUniversityAccount || organizationSlug !== tenant.slug);

  if (
    tenant.isLoading ||
    (isAuthenticated && isUserLoading)
  ) {
    return (
      <div className="min-h-screen bg-white flex justify-center items-center">
        <Spinner className="size-12" />
      </div>
    );
  }

  if (tenant.error) {
    if (isAuthenticated && user) {
      return <Navigate to={(routing?.homePath ?? "/dashboard")} replace />;
    }

    return (
      <div className="min-h-screen bg-white flex flex-col justify-center items-center gap-4 p-8">
        <h1 className="text-2xl font-bold text-black">Portal Not Found</h1>
        <p className="text-black text-center max-w-md">
          The portal you are looking for does not exist. Please check the URL
          and try again.
        </p>
      </div>
    );
  }

  if (hasTenantMismatch) {
    const tenantName =
      tenant.displayName ?? tenant.name ?? tenant.slug ?? "this portal";

    return (
      <div className="min-h-screen bg-white flex flex-col justify-center items-center gap-6 p-8">
        <h1 className="text-2xl font-bold text-black">Wrong Portal Account</h1>
        <p className="text-black text-center max-w-md">
          You are currently logged in to a different account context.
          Please log out to continue to <strong>{tenantName}</strong>.
        </p>
        <Button onClick={() => logout()}>
          Log out
        </Button>
      </div>
    );
  }

  return <Outlet />;
}
